import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { firstValueFrom, tap } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { StandaloneBaseComponent } from 'projects/shared/lib/components/standalone/base/standalone-base.component';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { Params } from '@angular/router';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { EError } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { ICookieBannerService, ICookieBannerServiceToken } from 'shared/services/ui/cookie-banner/cookie-banner-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';
import { ISidebarCartService, ISidebarCartServiceToken } from 'shared/services/ui/sidebar/cart/sidebar-cart-service.interface';

@Component({
  template: '',
  standalone: false
})
export class LoginBaseComponent extends StandaloneBaseComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('emailInputField') emailInput: ElementRef | undefined;
  public customerNumber: string;
  public coUserOrEmailOrAlias: string;
  public password: string;
  public passwordShow: boolean;

  protected authorizedLandingPage: INavigationEnvironment;
  public loginRequiresCookieAcceptance: boolean;

  protected readonly userService: IUserService = inject(IUserServiceToken);
  protected authService: IAuthService;
  protected readonly toastService: IToastService = inject(IToastServiceToken);
  protected readonly cookieBannerService: ICookieBannerService = inject(ICookieBannerServiceToken);
  private readonly sidebarCartService: ISidebarCartService = inject(ISidebarCartServiceToken);

  public constructor() {
    super();
    this.authService = inject(IAuthServiceToken);

    let title = $localize`:@@LoginTitle:Login`;
    this.titleSet(title);
    this.metaDescriptionSet([{ key: "page", value: title }]);

    this.customerNumber = '';
    this.coUserOrEmailOrAlias = '';
    this.password = '';
    this.passwordShow = false;

    this.authorizedLandingPage = ENVIRONMENT.authorizedLandingPage;
    ServiceLocator.injector.get(IConfigurationServiceToken).authorizedLandingPageGet().subscribe((targetPage) => {
      this.authorizedLandingPage.routerlink = targetPage.routerlink as string;
      let queryParams = new URLSearchParams(targetPage.queryParams);
      let queryParamsObject: Params = {};
      queryParams.forEach((value, key) => {
        queryParamsObject[key] = value;
      });
      this.authorizedLandingPage.queryParams = queryParamsObject;
    });

    this.loginRequiresCookieAcceptance = ENVIRONMENT.auth.loginRequiresCookiesAcceptance;

    this.cookieBannerService.cookieBannerShow();
    this.sidebarCartService.sidebarShoppingCartHide();
  }

  public ngOnInit(): void {
    this.authSubscriptionInit();
  }

  public ngAfterViewInit(): void {
    this.emailInput?.nativeElement.focus();
  }

  private authSubscriptionInit() {
    this.authService.authenticatedGet().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(
        (isAuthenticated) => {
          if (isAuthenticated == true) {
            this.router.navigate([this.authorizedLandingPage.routerlink], { queryParams: this.authorizedLandingPage.queryParams });
          }
        }
      )
    ).subscribe();
  }

  public passwordShowClick(): void {
    this.passwordShow = !(this.passwordShow);
  }

  public async loginClickAsync(): Promise<void> {
    let cookieAcceptanceState: boolean = this.authService.hasAcceptedCookieTerms();

    if (this.loginRequiresCookieAcceptance && cookieAcceptanceState == false) {
      this.cookieBannerService.cookieBannerShow();

      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        message: $localize`:@@LoginMissingCookieConsentMessage:Please accept the necessary cookies before your next login attempt.`,
        code: ErrorCodeService.formatErrorCode(EError.F200050),
      }));

      return;
    }

    let form = document.getElementById('loginForm') as HTMLFormElement;
    if (form.checkValidity() === false) {
      form.classList.add('was-validated');
      return;
    }

    await firstValueFrom(this.userService.userLogin(this.customerNumber, this.coUserOrEmailOrAlias, this.password)).then((accessJWT) => {
      if (accessJWT == undefined) {
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Danger,
          title: $localize`:@@LoginErrorToastTitle:Login failed`,
          message: $localize`:@@LoginErrorToastMessage:Your login attempt failed.`,
          code: ErrorCodeService.formatErrorCode(EError.F200060),
        }));
        return;
      }

      let isLoginSuccessfull = this.authService.isAccessJWTValid(accessJWT);
      if (isLoginSuccessfull != true) {
        throw new Error("Login failed. JWT validation failed.");
      }

      this.authService.accessJWTSet(accessJWT);

      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Success,
        title: $localize`:@@LoginSuccessToastTitle:Login successful`,
        message: $localize`:@@LoginSuccessToastMessage:Welcome to our eShop`,
      }));

      this.router.navigate([this.authorizedLandingPage.routerlink], { queryParams: this.authorizedLandingPage.queryParams });
    }).catch((ex) => {
      this.logger.error(ex, this.constructor.name);
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@LoginErrorToastTitle:Login failed`,
        message: $localize`:@@LoginErrorToastMessage:Your login attempt failed.`,
        code: JSON.parse(ex.response).errorCode
      }));
    });
  }

}
